<script setup lang="ts">
import type { ChartOptions, ChartData } from 'chart.js'
import type { PropType } from 'vue'
import { Pie } from 'vue-chartjs'
import merge from 'lodash/merge'
import cloneDeep from 'lodash/cloneDeep'

type PieOptions = ChartOptions<'pie'>

const props = defineProps({
  defaultOptions: {
    type: Object as PropType<PieOptions>,
    required: true,
  },
  visualizerOptions: {
    type: Object,
    required: true,
  },
  chartClasses: {
    type: String,
    required: true,
  },
  legendClasses: {
    type: String,
    default: '',
  },
  data: {
    type: Object as PropType<ChartData<'pie'>>,
    required: true,
  },
})

const emit = defineEmits(['chartRef'])

const chartRef = ref()
const options = merge(cloneDeep(props.defaultOptions), {
  scales: {
    y: {
      grid: {
        display: false,
        drawBorder: false,
      },
      ticks: {
        display: false,
      },
    },

    x: {
      grid: {
        drawBorder: false,
      },
      ticks: {
        display: false,
      },
    },
  },

  plugins: {
    tooltip: {
      mode: 'point',
    },
  },
} as PieOptions)

/*
  Legend color can also be a patterns or gradients,
  which are objects. We currently don't support other
  but string color.

  See:
  - https://www.chartjs.org/docs/latest/general/colors.html
*/
const getBackgroundColor = (color) => {
  return typeof color === 'string' ? color : ''
}

const datasets = computed(() => {
  return props.data.labels.map((label, id) => ({
    label,
    backgroundColor: getBackgroundColor(
      props.data.datasets[0].backgroundColor[id],
    ),
  }))
})

const onClickLegendItem = (labelIndex, isVisible) => {
  chartRef.value.chart.toggleDataVisibility(labelIndex, isVisible)
  chartRef.value.chart.update()
}
</script>

<template>
  <div>
    <div class="relative">
      <Pie
        ref="chartRef"
        :class="chartClasses"
        :data="data"
        :options="options"
        @vue:mounted="emit('chartRef', chartRef)"
      />
    </div>
    <ChartLegend
      v-if="props.visualizerOptions.legend.position !== 'none'"
      :class="legendClasses"
      :datasets="datasets"
      @click-legend-item="onClickLegendItem"
    />
  </div>
</template>
